/**
 * MIST by BDQ design tokens (R-VISUAL-01..R-VISUAL-05).
 *
 * These typed tokens are the single source of truth consumed by web, email,
 * OG-image generation, Blender label rendering, and the SEO pipeline. The
 * sibling `tokens.css` mirrors every value as a `--mist-*` custom property; the
 * brand-content contract test fails if the two drift apart.
 *
 * The classical dark-luxury palette moves toward deeper tones and refined gem
 * backdrop tints for each birthstone month.
 */

/** Colour, grouped by the visual semantics of R-VISUAL-04. */
export const color = {
  /** Structure and readability. */
  structure: {
    nearBlack: '#070707',
    ink: '#17171B',
    ivory: '#F7F3EC',
    ivorySoft: '#E7E0D0',
  },
  /** Emphasis and commerce affordances. */
  emphasis: {
    agedGold: '#DFB76C',
    agedGoldDeep: '#B08A3E',
  },
  /** Product-specific scent identity. */
  scent: {
    olive: '#5B6234',
    peridot: '#A7C23C',
    peridotLight: '#CBDE86',
  },
  /** Light and refraction moments. */
  highlight: {
    highlightWhite: '#FBFAF4',
    highlightGold: '#E9CE8C',
  },
  /** Birthstone backdrop tints — 12 months × Pour Femme / Pour Homme. */
  gems: {
    garnetFemme: '#5A0000',
    garnetHomme: '#2B0000',
    amethystFemme: '#4A154B',
    amethystHomme: '#220025',
    aquamarineFemme: '#1F4E5B',
    aquamarineHomme: '#0A232A',
    diamondFemme: '#3A3A3A',
    diamondHomme: '#1A1A1A',
    emeraldFemme: '#0A3A1A',
    emeraldHomme: '#031A0B',
    pearlFemme: '#4A3F35',
    pearlHomme: '#211A14',
    rubyFemme: '#600010',
    rubyHomme: '#300008',
    peridotGemFemme: '#3B4A00',
    peridotGemHomme: '#1B2200',
    sapphireFemme: '#001A4A',
    sapphireHomme: '#000B22',
    opalFemme: '#3D354A',
    opalHomme: '#1B1622',
    topazFemme: '#4A2E00',
    topazHomme: '#221400',
    turquoiseFemme: '#003A4A',
    turquoiseHomme: '#001A22',
  },
} as const

/** Maps each colour token to the kebab-case `--mist-color-*` variable name in tokens.css. */
export const colorCssVariable = {
  nearBlack: '--mist-color-near-black',
  ink: '--mist-color-ink',
  ivory: '--mist-color-ivory',
  ivorySoft: '--mist-color-ivory-soft',
  agedGold: '--mist-color-aged-gold',
  agedGoldDeep: '--mist-color-aged-gold-deep',
  olive: '--mist-color-olive',
  peridot: '--mist-color-peridot',
  peridotLight: '--mist-color-peridot-light',
  highlightWhite: '--mist-color-highlight-white',
  highlightGold: '--mist-color-highlight-gold',
} as const

/** Flattens the grouped colour tokens into `{ nearBlack: '#0B0B0D', ... }`. */
export function flattenColorTokens(): Record<string, string> {
  const flat: Record<string, string> = {}
  for (const group of Object.values(color)) {
    for (const [name, hex] of Object.entries(group)) {
      flat[name] = hex
    }
  }
  return flat
}

/**
 * Typography (R-VISUAL-02). Amiri is reserved exclusively for accurate Arabic
 * glosses; it must never be assigned to a Latin-text role.
 */
export const fontFamilies = {
  display: 'Cinzel',
  label: 'Cinzel',
  body: 'Cormorant Garamond',
  editorialItalic: 'Cormorant Garamond',
  archivalData: 'JetBrains Mono',
  arabic: 'Amiri',
} as const

/** The one font permitted for Arabic glosses, and nothing else. */
export const arabicOnlyFont = 'Amiri'

/** Spacing scale on a 4px base. */
export const space = {
  0: '0',
  1: '4px',
  2: '8px',
  3: '12px',
  4: '16px',
  5: '20px',
  6: '24px',
  8: '32px',
  10: '40px',
  12: '48px',
  16: '64px',
  20: '80px',
  24: '96px',
  32: '128px',
} as const

/** Corner radii. */
export const radius = {
  none: '0',
  sm: '2px',
  md: '6px',
  lg: '12px',
  pill: '999px',
} as const

/**
 * Motion. Easing curves are exponential ease-out families only — no bounce,
 * elastic, or back overshoot, per the quiet/architectural posture (R-VISUAL-03).
 */
export const motion = {
  easing: {
    entrance: 'cubic-bezier(0.22, 1, 0.36, 1)',
    exit: 'cubic-bezier(0.64, 0, 0.78, 0)',
    standard: 'cubic-bezier(0.4, 0, 0.2, 1)',
  },
  duration: {
    fast: '160ms',
    base: '280ms',
    slow: '520ms',
  },
} as const

export type ColorTokens = typeof color
export type FontFamilies = typeof fontFamilies
